import React, { useContext, useEffect, useState } from 'react';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import { globalResponseToFrameworkOrder } from '@app/pages/customer/supplier-notification/snFrameworkOrders';
import { useSearchParams } from 'react-router-dom';
import CreateSupplierNotification, { SupplierNotificationForm } from '@app/pages/customer/supplier-notification/CreateSupplierNotification.tsx';
import { docNumbersFromShipmentId } from '@app/pages/customer/supplier-notification/utils.ts';
import ResponsiveLayout from '@app/layout/ResponsiveLayout.tsx';

interface ComponentProps {
}

const CreateSupplierNotificationPage: React.FC<ComponentProps> = () => {
  const { tr } = useTranslationLgs();
  const [initialData, setInitialData] = useState<SupplierNotificationForm>();
  const { cuGetSupplierNotificationForDuplication } = useContext(ApiContext);

  const [searchParams] = useSearchParams();
  const fromSupplierNotificationId = searchParams.get('fromSupplierNotificationId');

  useEffect(() => {
    cuGetSupplierNotificationForDuplication(fromSupplierNotificationId, d => {
      const frameworkOrders = d.orders.map(globalResponseToFrameworkOrder);
      const cargoType = { cargoTypeCode: d.cargoType.cargoType.cargoTypeCode, adrWeight: d.cargoType.adrWeight };
      // Typed doc fields are validated (not the legacy shipmentId); fall back to shipmentId for older source SNs that only carry it.
      const fallbackDocs = docNumbersFromShipmentId(d.transportationType, d.shipmentId);

      setInitialData({
        cargoType,
        companyId: d.customerId,
        supplierId: d.supplierId,
        attachments: d.attachmentsData,
        transportationType: d.transportationType,
        supplierLocationId: d.supplierLocationId,
        customerLocationId: d.customerLocationId,
        shipmentStateCode: d.shipmentStateCode,
        specialRequirementIds: d.specialRequirementCodes,
        containerInfo: d.containerInfo,
        customsTypeCode: d.customsTypeCode,
        cmrNumber: d.cmrNumber ?? fallbackDocs.cmrNumber,
        blNumber: d.blNumber ?? fallbackDocs.blNumber,
        awbNumber: d.awbNumber ?? fallbackDocs.awbNumber,
        referralCode: d.referralCode,
        internalNote: d.internalNote,
        notifyData: {
          notifyUsersDependsOnRole: d.notifyData.notifyUsersDependsOnRole,
          responsiblePerson: d.notifyData.responsiblePerson,
          userToNotify: d.notifyData.usersToNotify,
        },
        orders: frameworkOrders,
        goodsInvoice: d.internalInfoData?.internalInvoiceNumber
          ? { goodsInvoiceNumber: d.internalInfoData.internalInvoiceNumber, goodsInvoiceDate: d.internalInfoData.invoiceDueDate }
          : undefined,
        deliveryConditionsIncotermCode: d.internalInfoData?.deliveryConditionsIncotermCode,
        shipmentId: d.shipmentId,
        fromShipmentId: null,
        customerLocation: d.customerLocation,
        supplierLocation: d.supplierLocation,
        supplierDeliverySlots: { dayWithInterval: [], timezoneId: null },
        customerDeliverySlots: { dayWithInterval: [], timezoneId: null },
        counterPartyNotifyPersonsIds: [],
        labels: d.labels ?? [],
      });
    });
  }, []);

  return (
    <>
      <ResponsiveLayout title={tr(`create-shipment.createSupplierNotification`, `Duplicate supplier notification`)}>
        {initialData && <CreateSupplierNotification initialData={initialData} />}
      </ResponsiveLayout>
    </>
  );
};

export default withAuthenticationRequired(CreateSupplierNotificationPage);
